// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { paginatedResultSchema } from '@/api/paging';
import { sparseAssetSchema } from '@/asset/api/types';
import { caseInsensitiveLowerEnum } from '@/constants/case-insensitive-enum';
import { trackingNumberDetailsSchema } from '@/constants/shipping-carrier';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

const movementDisplayStatusSchema = caseInsensitiveLowerEnum([
  'open',
  'in_transit',
  'delivered',
]);

export const movementStatusSchema = z.enum([
  'open',
  'in_transit',
  'delivered',
  'all',
]);

export type MovementStatus = z.infer<typeof movementStatusSchema>;

const movementLocationSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string().nullable(),
  isTransient: z.boolean(),
  address: z
    .object({
      formattedAddress: z.string().nullable(),
    })
    .nullable(),
});

const movementSchema = z.object({
  id: z.string(),
  key: z.string(),
  asset: sparseAssetSchema,
  originLocation: movementLocationSchema.nullable(),
  destinationLocation: movementLocationSchema.nullable(),
  trackingNumberDetails: trackingNumberDetailsSchema.nullable(),
  status: movementDisplayStatusSchema,
  shippedAt: dateTimeSchema.nullable(),
  deliveredAt: dateTimeSchema.nullable(),
  expectedShippingDate: dateSchema.nullable(),
  expectedDeliveryDate: dateSchema.nullable(),
  lastActivityAt: dateTimeSchema,
  shipmentNumber: z.string().nullable(),
});

export type Movement = z.infer<typeof movementSchema>;

export const pagedMovementSchema = paginatedResultSchema(movementSchema);
